/**
 * Teacher appreciation cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const teacherAppreciationCardsPack: ToilePack = {
    slug: 'teacher-appreciation-cards',
    category: 'season',
    name: 'Teacher appreciation cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'thanks', label: 'Thank you card', subject: 'a vertical card lettered "Thank You, Teacher" over a red apple and a stack of books, a clear empty panel below for a message'},
        {key: 'pencil', label: 'Pencil pun', subject: 'a card with a large yellow pencil and the line "Thanks for helping me write my story", three ruled lines at the bottom for a signature'},
        {key: 'class', label: 'From the class', subject: 'a wide card headed "Thank You from Our Class" with a large empty field ruled into small boxes where every pupil signs their name'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor illustration, loose edges, hand-lettered text', palette: 'apple red, school-bus yellow and leaf green washes on white paper', framing: 'Centered on the page with a wide white margin'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'red, yellow, navy and cream, a limited palette', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'chalkboard', label: 'Chalkboard', style: 'hand-drawn chalk illustration on a slate board, chalk lettering, smudged dust', palette: 'white and pastel chalk on dark green slate', framing: 'Centered on the card with a slate border all round'},
    ],
    directions: ['Apple', 'Bus yellow', 'Gold stars'],
    directionHint: 'try: Apple',
    seo: {
        title: 'Teacher Appreciation Cards: Thank You, Pencil, Class',
        metaDescription: 'Printable teacher appreciation cards with a thank-you message, a pencil pun and a whole-class card, in three styles.',
        h1: 'Teacher appreciation cards: thank you, pencil pun, from the class',
        tagline: "Nine printable thank-you cards, each with the thanks spelled exactly and room for the child's own words.",
        intro: "Printable thank-you cards a pupil or parent gives a teacher in Teacher Appreciation Week or at the end of the school year. Each card has the thanks spelled exactly: over an apple and books, beside a pencil, or on a class card with boxes for every pupil to sign. A blank area is left for the child's own words. Watercolor, flat color or chalkboard. Type a different wording, and the same card carries it.",
        useCases: [
            'a card from a pupil at the end of the school year',
            'a card signed by a whole class',
            'a card from a parent during Teacher Appreciation Week',
            'a chalkboard-style card for a classroom display',
        ],
        faq: [
            {q: 'Can every pupil sign the class card?', a: "Yes. The class card has a large field ruled into small boxes, one for each pupil's name."},
            {q: 'What goes in the blank panel?', a: 'The child writes their own words. The panel is left empty on the printed card.'},
            {q: 'Which style suits a classroom wall?', a: 'The chalkboard style draws the card on dark green slate with chalk lettering.'},
        ],
    },
}
